/**
 * Prefetches critical data immediately after the user authenticates.
 *
 * @module hooks/usePrefetchOnLogin
 *
 * @remarks
 * Fires once on the `false → true` transition of `auth.isAuthenticated`.
 * Seeding these cache entries on login means the Cases and Employees
 * pages render instantly on first navigation — no loading spinners.
 *
 * Call this hook inside a component that lives within both
 * `QueryClientProvider` and `AppProvider` (e.g. `OAuthBridge`).
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useApp } from "../app/Application.provider";
import { caseKeys } from "../features/cases/queryKeys";
import type { PaginatedResponse } from "../features/cases/types";
import { employeeKeys } from "../features/employees/queryKeys";
import { apiClient } from "../lib/apiClient";
import type { Employee, Case } from "../types/loa";
// NOTE: `keywordKeys` and `KeywordsMap` will return when the /keywords
// prefetch is re-enabled below (see TODO inside the hook).

export function usePrefetchOnLogin() {
  const { auth } = useApp();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    queryClient.prefetchQuery({
      queryKey: caseKeys.list({}),
      queryFn: () => apiClient.get<PaginatedResponse<Case>>("/cases/").then((r) => r.data.results),
    });

    // TODO(api): Re-enable once backend ships GET /api/v1/keywords.
    // Disabled because the route does not exist yet — calling it produces a
    // browser-level 404 log on every login that a JS .catch() cannot suppress
    // (DevTools always reports 4xx responses regardless of how JS handles
    // them). The UI tolerates an empty/missing keyword map.
    //
    // queryClient.prefetchQuery({
    //   queryKey: keywordKeys.all(),
    //   queryFn: () => apiClient.get<KeywordsMap>("/keywords").then((r) => r.data),
    // });

    // Prefetch the first page of the paginated employee list.
    // Uses the same key shape and default params (page 1, 10 rows) that
    // useEmployees produces on initial load, so the Employees page avoids
    // a loading spinner on first navigation when no filters are active.
    queryClient.prefetchQuery({
      queryKey: employeeKeys.paginatedList({ page: 1, page_size: 10 }),
      queryFn: () =>
        apiClient
          .get<PaginatedResponse<Employee>>("/employees/", { params: { page: 1, page_size: 10 } })
          .then((r) => r.data),
    });
  }, [auth.isAuthenticated, queryClient]);
}
